/**
 * Money is always an integer number of cents. Quantities are decimals with up to three places,
 * rates are integer basis points (21 % = 2100). Rounding is half away from zero, done once per
 * step with integer arithmetic, so no floating-point error can reach a stored amount.
 */
const MAX_SAFE_CENTS = Number.MAX_SAFE_INTEGER;
export class MoneyError extends Error {
}
function assertCents(value, what) {
    if (!Number.isSafeInteger(value))
        throw new MoneyError(`${what} must be a whole number of cents`);
}
/** Divides with rounding half away from zero. */
export function divRound(numerator, denominator) {
    if (denominator === 0n)
        throw new MoneyError('division by zero');
    const negative = (numerator < 0n) !== (denominator < 0n);
    const n = numerator < 0n ? -numerator : numerator;
    const d = denominator < 0n ? -denominator : denominator;
    const rounded = (n * 2n + d) / (2n * d);
    return negative ? -rounded : rounded;
}
function toSafeCents(value, what) {
    if (value > BigInt(MAX_SAFE_CENTS) || value < -BigInt(MAX_SAFE_CENTS))
        throw new MoneyError(`${what} is too large`);
    return Number(value);
}
/**
 * Parses a decimal written the Lithuanian or English way ("1 234,56", "1234.56", "-5", "2,5")
 * into an integer scaled by 10^places. Returns null for anything that is not a plain number.
 */
export function parseDecimal(text, places) {
    const cleaned = text.trim().replace(/[\s  €]/g, '').replace(/^−/, '-');
    if (cleaned === '')
        return null;
    let normalised = cleaned;
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    if (lastComma !== -1 && lastDot !== -1) {
        // Whichever separator comes last is the decimal one; the other groups thousands.
        normalised = lastComma > lastDot ? cleaned.replace(/\./g, '').replace(',', '.') : cleaned.replace(/,/g, '');
    }
    else if (lastComma !== -1) {
        normalised = cleaned.replace(',', '.');
    }
    const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(normalised);
    if (!match)
        return null;
    const [, sign, whole, fraction = ''] = match;
    if (fraction.length > places) {
        // More digits than allowed: round the extra part half away from zero.
        const scaled = BigInt(whole + fraction);
        const value = divRound(scaled, 10n ** BigInt(fraction.length - places));
        return sign === '-' ? -value : value;
    }
    const value = BigInt(whole + fraction.padEnd(places, '0'));
    return sign === '-' ? -value : value;
}
/** "1 234,56" → 123456. Throws on text that is not an amount. */
export function parseMoney(text) {
    const value = parseDecimal(text, 2);
    if (value === null)
        throw new MoneyError(`not an amount: ${text}`);
    return toSafeCents(value, 'amount');
}
/** Quantity in thousandths: "2,5" → 2500. */
export function parseQuantity(text) {
    const value = parseDecimal(text, 3);
    if (value === null)
        throw new MoneyError(`not a quantity: ${text}`);
    return toSafeCents(value, 'quantity');
}
/** Rate in basis points: "21" → 2100, "5,5" → 550. */
export function parseRate(text) {
    const value = parseDecimal(text, 2);
    if (value === null)
        throw new MoneyError(`not a rate: ${text}`);
    return toSafeCents(value, 'rate');
}
/** quantity (thousandths) × unit price (cents) → cents. */
export function lineAmount(quantityMilli, unitPrice) {
    assertCents(quantityMilli, 'quantity');
    assertCents(unitPrice, 'unit price');
    return toSafeCents(divRound(BigInt(quantityMilli) * BigInt(unitPrice), 1000n), 'line amount');
}
/** amount × rate (basis points) → cents. */
export function applyRate(amount, rateBp) {
    assertCents(amount, 'amount');
    assertCents(rateBp, 'rate');
    return toSafeCents(divRound(BigInt(amount) * BigInt(rateBp), 10000n), 'rate amount');
}
/** 123456 → "1 234,56 €" (Lithuanian style; non-breaking thin grouping is left to the UI). */
export function formatMoney(amount, currency = 'EUR') {
    assertCents(amount, 'amount');
    const negative = amount < 0;
    const abs = Math.abs(amount);
    const whole = Math.trunc(abs / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const fraction = (abs % 100).toString().padStart(2, '0');
    return `${negative ? '−' : ''}${whole},${fraction} ${currency === 'EUR' ? '€' : currency}`;
}
/** 2500 → "2,5"; 1000 → "1". */
export function formatQuantity(quantityMilli) {
    const negative = quantityMilli < 0;
    const abs = Math.abs(quantityMilli);
    const whole = Math.trunc(abs / 1000);
    const fraction = (abs % 1000).toString().padStart(3, '0').replace(/0+$/, '');
    return `${negative ? '-' : ''}${whole}${fraction ? ',' + fraction : ''}`;
}
//# sourceMappingURL=money.js.map